/**
 * Thin axios wrapper around the SMS / voter-messaging endpoints under
 * `/api/messaging/*`. Mirrors the pattern in `src/api/dataChanges.ts`:
 * bare `client.get/post/patch` + typed returns + async/await + the
 * unwrapped `.data` value (no AxiosResponse leaks).
 *
 * Surface:
 *   - P2P: queue + send-one
 *   - Batch: preview + create-campaign
 *   - Campaign management: list / detail / pause / resume / cancel
 *     (read+control endpoints aren't all live yet — type them now so
 *     views compile against the contract; views handle 404 gracefully)
 *   - Per-voter: conversation thread + consent-ledger
 *
 * The P2P send path catches HTTP 429 and converts it into a typed
 * `P2pRateLimitedError` so callers can surface a "you're sending too
 * fast — wait N seconds" toast instead of a generic axios error. The
 * backend writes `Retry-After` as integer seconds (per its rate-limiter
 * impl); HTTP-date format is documented but not currently used.
 */
import axios from 'axios';
import client from '@/api/client';
import type {
  P2pQueueResponse,
  P2pSendRequest,
  P2pSendResponse,
  BatchPreviewRequest,
  BatchPreviewResponse,
  BatchSendRequest,
  BatchSendResponse,
  VoterCampaign,
  VoterMessage,
  SmsConsentEvent,
} from '@/types/messaging';

// ---------------------------------------------------------------------------
// Custom error for the P2P rate-limit case. Callers can `instanceof` check.
// ---------------------------------------------------------------------------

export class P2pRateLimitedError extends Error {
  constructor(
    public retryAfterSeconds: number,
    public window: 'minute' | 'hour' | 'unknown' = 'unknown',
  ) {
    super(`P2P rate limit exceeded (${window})`);
    // Restore prototype chain for `instanceof` to work after transpilation.
    Object.setPrototypeOf(this, P2pRateLimitedError.prototype);
    this.name = 'P2pRateLimitedError';
  }
}

// ---------------------------------------------------------------------------
// P2P: queue + send-one
// ---------------------------------------------------------------------------

/**
 * GET /api/messaging/p2p/queue
 *
 * `audience` is a comma-separated list of precinct numbers (passed
 * through as-is by the server). `limit` caps queue size.
 */
export async function fetchP2pQueue(opts?: {
  audience?: string;
  limit?: number;
}): Promise<P2pQueueResponse> {
  const params: Record<string, string | number> = {};
  if (opts?.audience) params.audience = opts.audience;
  if (opts?.limit !== undefined) params.limit = opts.limit;
  const res = await client.get<P2pQueueResponse>('/api/messaging/p2p/queue', {
    params,
  });
  return res.data;
}

/**
 * POST /api/messaging/p2p/send
 *
 * Sends one SMS to one voter. On HTTP 429 the server returns a
 * `Retry-After` header (integer seconds) and an optional
 * `X-RateLimit-Window` header indicating which bucket tripped
 * ('minute' or 'hour'). Converted into a typed `P2pRateLimitedError`.
 */
export async function sendP2pMessage(
  payload: P2pSendRequest,
): Promise<P2pSendResponse> {
  try {
    const res = await client.post<P2pSendResponse>(
      '/api/messaging/p2p/send',
      payload,
    );
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 429) {
      const headers = err.response.headers ?? {};
      const retryAfterRaw =
        (headers['retry-after'] as string | undefined) ??
        (headers['Retry-After'] as string | undefined);
      const retryAfter = Number.parseInt(retryAfterRaw ?? '', 10);
      const windowHeader =
        (headers['x-ratelimit-window'] as string | undefined) ??
        (headers['X-RateLimit-Window'] as string | undefined);
      const window: 'minute' | 'hour' | 'unknown' =
        windowHeader === 'minute' || windowHeader === 'hour'
          ? windowHeader
          : 'unknown';
      throw new P2pRateLimitedError(
        Number.isFinite(retryAfter) ? retryAfter : 60,
        window,
      );
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Batch: preview audience + create campaign
// ---------------------------------------------------------------------------

/**
 * POST /api/messaging/batch/preview
 *
 * Dry-run audience resolution: returns total + eligible counts plus a
 * small sample of recipients. No messages are sent and no campaign row
 * is created.
 */
export async function previewBatch(
  payload: BatchPreviewRequest,
): Promise<BatchPreviewResponse> {
  const res = await client.post<BatchPreviewResponse>(
    '/api/messaging/batch/preview',
    payload,
  );
  return res.data;
}

/**
 * POST /api/messaging/batch/send
 *
 * Creates the campaign row + queues recipients. `consent_attested`
 * MUST be true or the server returns 400. Pass `scheduled_at: null`
 * (or omit) to send immediately.
 */
export async function sendBatch(
  payload: BatchSendRequest,
): Promise<BatchSendResponse> {
  const res = await client.post<BatchSendResponse>(
    '/api/messaging/batch/send',
    payload,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Campaign list / detail / lifecycle
//
// These endpoints aren't all live yet. Types are speculative-but-stable
// so the campaign-list and campaign-detail views can compile and ship
// behind a feature flag. Views should treat 404 as "no campaigns yet"
// and render an empty state.
// ---------------------------------------------------------------------------

/**
 * GET /api/messaging/batch/campaigns
 */
export async function listCampaigns(opts?: {
  status?: string;
  limit?: number;
}): Promise<VoterCampaign[]> {
  const params: Record<string, string | number> = {};
  if (opts?.status) params.status = opts.status;
  if (opts?.limit !== undefined) params.limit = opts.limit;
  const res = await client.get<VoterCampaign[]>(
    '/api/messaging/batch/campaigns',
    { params },
  );
  return res.data;
}

/**
 * GET /api/messaging/batch/campaigns/{id}
 */
export async function getCampaign(id: number): Promise<VoterCampaign> {
  const res = await client.get<VoterCampaign>(
    `/api/messaging/batch/campaigns/${id}`,
  );
  return res.data;
}

/**
 * PATCH /api/messaging/batch/campaigns/{id}/pause
 */
export async function pauseCampaign(id: number): Promise<VoterCampaign> {
  const res = await client.patch<VoterCampaign>(
    `/api/messaging/batch/campaigns/${id}/pause`,
  );
  return res.data;
}

/**
 * PATCH /api/messaging/batch/campaigns/{id}/resume
 */
export async function resumeCampaign(id: number): Promise<VoterCampaign> {
  const res = await client.patch<VoterCampaign>(
    `/api/messaging/batch/campaigns/${id}/resume`,
  );
  return res.data;
}

/**
 * PATCH /api/messaging/batch/campaigns/{id}/cancel
 */
export async function cancelCampaign(id: number): Promise<VoterCampaign> {
  const res = await client.patch<VoterCampaign>(
    `/api/messaging/batch/campaigns/${id}/cancel`,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Per-voter: conversation thread + consent ledger
// ---------------------------------------------------------------------------

/**
 * GET /api/messaging/voters/{voter_id}/messages
 */
export async function fetchVoterMessages(
  voterId: number,
): Promise<VoterMessage[]> {
  const res = await client.get<VoterMessage[]>(
    `/api/messaging/voters/${voterId}/messages`,
  );
  return res.data;
}

/**
 * GET /api/messaging/consent/events?voter_id={id}
 */
export async function fetchConsentEvents(
  voterId: number,
): Promise<SmsConsentEvent[]> {
  const res = await client.get<SmsConsentEvent[]>(
    '/api/messaging/consent/events',
    { params: { voter_id: voterId } },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Manual consent override (admin-only)
//
// These endpoints may not be live yet. Callers can `instanceof` check for
// `EndpointNotImplementedError` to render an inline "coming soon" hint and
// disable the action button. Any other error propagates as-is.
// ---------------------------------------------------------------------------

export class EndpointNotImplementedError extends Error {
  constructor(public endpoint: string) {
    super(`Endpoint not implemented: ${endpoint}`);
    Object.setPrototypeOf(this, EndpointNotImplementedError.prototype);
    this.name = 'EndpointNotImplementedError';
  }
}

/**
 * POST /api/messaging/consent/opt-in
 *
 * Admin-only manual opt-in. Writes a row to the consent ledger with the
 * acting user's id + timestamp. Throws `EndpointNotImplementedError` if
 * the backend returns 404 (endpoint not yet deployed).
 */
export async function manualOptIn(
  voterId: number,
  source: string,
  notes?: string,
): Promise<void> {
  try {
    await client.post('/api/messaging/consent/opt-in', {
      voter_id: voterId,
      source,
      notes: notes ?? null,
    });
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      throw new EndpointNotImplementedError('/api/messaging/consent/opt-in');
    }
    throw err;
  }
}

/**
 * POST /api/messaging/consent/opt-out
 *
 * Admin-only manual opt-out. Writes a row to the consent ledger with the
 * acting user's id + timestamp. Throws `EndpointNotImplementedError` if
 * the backend returns 404 (endpoint not yet deployed).
 */
export async function manualOptOut(
  voterId: number,
  source: string,
  notes?: string,
): Promise<void> {
  try {
    await client.post('/api/messaging/consent/opt-out', {
      voter_id: voterId,
      source,
      notes: notes ?? null,
    });
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      throw new EndpointNotImplementedError('/api/messaging/consent/opt-out');
    }
    throw err;
  }
}
